import React, { useState, useRef, useEffect } from 'react';
import ArrowIcon from '../../../assets/icons/arrow-down.svg';
import { ReactComponent as Selected } from '../../../assets/icons/Radiobutton-s.svg';
import { ReactComponent as NotSelected } from '../../../assets/icons/Radiobutton-n.svg';

import './HealthSummary.scss';
import { Answer } from '../CompleteProfileForm/FormQuestion';
import TextInput from '../../BaseComponents/TextInput';
import Select from '../../BaseComponents/Select';
import Button from '../../BaseComponents/Button';
import { useDispatch, useSelector } from 'react-redux';
import {
  MedicalAnswerRequest,
  MedicalAnswerResponseElement,
  MedicalQuestionResponseElement,
} from '../../../types/apiGeneratedTypes';
import { formatInTimeZone } from 'date-fns-tz';
import { answerMedicalQuestion } from '../../../api/medicalQuestions';
import { setQuestionsList } from '../../../store/medicalQuestionsSlice';
import { RootState } from '../../../store/store';

type Props = {
  questionData: MedicalQuestionResponseElement;
  activeQuestion: number | null;
  setActiveQuestion: (id: number | null) => void;
  setCompletedQuestions: (value: any) => void;
};

const MedicalQuestion = ({
  activeQuestion,
  questionData,
  setActiveQuestion,
  setCompletedQuestions,
}: Props) => {
  const dispatch = useDispatch();
  const isExpanded = activeQuestion === questionData.id;
  const [height, setHeight] = useState(0);
  const [selected, setSelected] = useState<string | null>('no');
  const [inputValue, setInputValue] = useState<any>('');
  const [selectedUnit, setSelectedUnit] = useState<any>(
    questionData.value_units.length
      ? { key: questionData.value_units[0], value: questionData.value_units[0] }
      : null
  );
  const [isSubmitted, setIsSubmitted] = useState<boolean>(
    questionData.answers.length > 0
  );
  const [isLoading, setIsLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [previousAnswer, setPreviousAnswer] =
    useState<MedicalAnswerResponseElement>(
      questionData.answers.length
        ? { ...questionData.answers[0] }
        : {
            question_id: questionData.id,
            timestamp: '',
            value: '',
            value_option: '',
            unit: '',
          }
    );

  const { memberData } = useSelector((state: RootState) => state.member);
  const { questionsList } = useSelector((state: RootState) => state.questions);

  useEffect(() => {
    if (isExpanded && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isExpanded, selected, inputValue]);

  const onQuestionClick = () => {
    if (isExpanded) {
      setActiveQuestion(null);
    } else {
      setActiveQuestion(questionData.id);
    }
  };

  const onAnswerChange = (answer: Answer) => {
    if (selected === 'yes' && answer !== 'yes') {
      setSelected(answer);
    }

    setSelected(answer);
  };

  const saveData = async () => {
    const requestObj: MedicalAnswerRequest = {
      member_id: memberData?.user_id!,
      question_id: questionData.id,
      value: inputValue.key ? inputValue.key : inputValue,
    };
    if (
      questionData.value_options &&
      questionData.value_options.length > 0 &&
      questionData.value_options.includes('yes') &&
      questionData.value_options.includes('no')
    ) {
      requestObj.value_option = selected;
      if (selected !== 'yes') requestObj.value = '';
    }
    if (questionData.value_units.length) {
      requestObj.unit = selectedUnit.key;
    }
    setIsLoading(true);
    try {
      const resp = await answerMedicalQuestion(requestObj);
      if (resp) {
        const newAnswer = {
          timestamp: new Date().toISOString(),
          value: inputValue.key ? inputValue.key : inputValue,
          value_option:
            questionData.value_options &&
            questionData.value_options.length > 0 &&
            questionData.value_options.includes('yes') &&
            questionData.value_options.includes('no') &&
            selected
              ? selected
              : '',
          unit: selectedUnit?.key ? selectedUnit.key : '',
          question_id: previousAnswer.question_id,
        };
        const newQuestionsList = {
          History: questionsList.History.map((question: any) => {
            if (question.id === questionData.id) {
              return {
                ...question,
                answers: [newAnswer],
              };
            }
            return question;
          }),
          MOCA: questionsList.MOCA.map((question: any) => {
            if (question.id === questionData.id) {
              return {
                ...question,
                answers: [newAnswer],
              };
            }
            return question;
          }),
          'PHQ-9': questionsList['PHQ-9'].map((question: any) => {
            if (question.id === questionData.id) {
              return {
                ...question,
                answers: [newAnswer],
              };
            }
            return question;
          }),
          'Physical Examination': questionsList['Physical Examination'].map(
            (question: any) => {
              if (question.id === questionData.id) {
                return {
                  ...question,
                  answers: [newAnswer],
                };
              }
              return question;
            }
          ),
        };
        dispatch(setQuestionsList(newQuestionsList));
        setPreviousAnswer(newAnswer);
        if (!isSubmitted) {
          setCompletedQuestions((prev: number) => prev + 1);
          setIsSubmitted(true);
        }
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getValueOption = () => {
    const correctAnswer =
      previousAnswer.value_option === 'third'
        ? 'no'
        : previousAnswer.value_option;

    return correctAnswer
      ? correctAnswer.charAt(0).toUpperCase() + correctAnswer.slice(1)
      : '-';
  };

  return (
    <div
      className='question-container'
      style={{ border: isExpanded ? '1px solid #4F9EAD' : undefined }}
    >
      <div
        className={`question-name ${isExpanded ? 'bold-name' : ''}`}
        onClick={onQuestionClick}
      >
        <div>{questionData.name}</div>
        <div className='status-and-arrow'>
          {isSubmitted && <div className='status'>Completed</div>}
          <img
            src={ArrowIcon}
            alt=''
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </div>
      </div>
      <div
        className='question-body'
        ref={contentRef}
        style={{
          height: `${height}px`,
          transition: 'height 0.2s ease',
          overflow: 'hidden',
          paddingBottom: isExpanded ? '24px' : '0',
        }}
      >
        {questionData.value_options &&
          questionData.value_options.length > 0 &&
          questionData.value_options.includes('yes') &&
          questionData.value_options.includes('no') && (
            <div className='yes-no-container'>
              <div className='user-answer-container'>
                <div
                  className={`answer yes ${
                    selected === 'yes' ? 'answer-active' : ''
                  }`}
                  onClick={() => onAnswerChange('yes')}
                >
                  {selected === 'yes' ? <Selected /> : <NotSelected />}
                  Yes
                </div>
                <div
                  className={`answer no ${
                    selected === 'no' ? 'answer-active' : ''
                  }`}
                  onClick={() => onAnswerChange('no')}
                >
                  {selected === 'no' ? <Selected /> : <NotSelected />}
                  No
                </div>
                <div
                  className={`answer third ${
                    selected === 'third' ? 'answer-active' : ''
                  }`}
                  onClick={() => onAnswerChange('third')}
                >
                  {selected === 'third' ? <Selected /> : <NotSelected />}
                  Prefer not to say
                </div>
              </div>
              <div className='input-container'>
                {(questionData.value_type === 'text' ||
                  questionData.value_type === 'integer') &&
                  selected === 'yes' && (
                    <TextInput
                      name='input'
                      placeholder={questionData.value_placeholder}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                  )}
                {questionData.value_units.length > 0 && selected === 'yes' && (
                  <div className='unit-container'>
                    <Select
                      name='units'
                      placeholder=''
                      selectedOption={selectedUnit}
                      options={questionData.value_units.reduce(
                        (acc: any, cur: any) => {
                          acc.push({ value: cur, key: cur });
                          return acc;
                        },
                        []
                      )}
                      onChange={(option) => setSelectedUnit(option)}
                      isError={false}
                    />
                  </div>
                )}
                {questionData.value_type === 'dropdown' && (
                  <Select
                    name='input'
                    placeholder={questionData.value_placeholder}
                    selectedOption={inputValue}
                    options={questionData.value_options.reduce(
                      (acc: any, cur: any) => {
                        acc.push({ value: cur, key: cur });
                        return acc;
                      },
                      []
                    )}
                    onChange={(option) => setInputValue(option)}
                    isError={false}
                    maxHeight='160px'
                  />
                )}
              </div>
              <div className='save-btn-container'>
                <Button
                  text='Save'
                  onClick={saveData}
                  isDisabled={selected === 'yes' && !inputValue}
                  isLoading={isLoading}
                />
              </div>
            </div>
          )}

        {questionData.value_options &&
          questionData.value_options.length > 0 &&
          questionData.value_type === 'dropdown' &&
          !questionData.value_options.includes('yes') &&
          !questionData.value_options.includes('no') && (
            <div className='yes-no-container'>
              <Select
                name='input'
                placeholder={questionData.value_placeholder}
                selectedOption={inputValue}
                options={questionData.value_options?.reduce(
                  (acc: any, cur: any) => {
                    acc.push({ value: cur, key: cur });
                    return acc;
                  },
                  []
                )}
                onChange={(option) => setInputValue(option)}
                isError={false}
                maxHeight='160px'
              />
              <div className='save-btn-container'>
                <Button
                  text='Save'
                  onClick={saveData}
                  isDisabled={!inputValue.key}
                  isLoading={isLoading}
                />
              </div>
            </div>
          )}
        {questionData.value_options &&
          !questionData.value_options.length &&
          (questionData.value_type === 'text' ||
            questionData.value_type === 'integer') && (
            <div className='yes-no-container'>
              <div className='input-container'>
                <TextInput
                  name='input'
                  placeholder={questionData.value_placeholder}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  isError={false}
                />
                {questionData.value_units.length > 0 &&
                  (!questionData.value_options.length ||
                    selected === 'yes') && (
                    <div className='unit-container'>
                      <Select
                        name='units'
                        placeholder=''
                        selectedOption={selectedUnit}
                        options={questionData.value_units.reduce(
                          (acc: any, cur: any) => {
                            acc.push({ value: cur, key: cur });
                            return acc;
                          },
                          []
                        )}
                        onChange={(option) => setSelectedUnit(option)}
                        isError={false}
                      />
                    </div>
                  )}
              </div>

              <div className='save-btn-container'>
                <Button
                  text='Save'
                  onClick={saveData}
                  isDisabled={!inputValue}
                  isLoading={isLoading}
                />
              </div>
            </div>
          )}
        <div className='previous-answer-container'>
          <div className='date'>
            {previousAnswer.timestamp
              ? formatInTimeZone(previousAnswer.timestamp, 'UTC', 'dd/MM/yyyy')
              : '-'}
          </div>
          <div className='answer'>{getValueOption()}</div>
          <div className='value'>
            {previousAnswer.value
              ? `${previousAnswer.value} ${
                  previousAnswer.unit ? previousAnswer.unit : ''
                }`
              : '-'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalQuestion;
